<template>
    <!-- Results of useSearchDropdown(), shared by the navbar search panel and the home search -->
    <div class="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50">
        <!-- Loading State -->
        <div v-if="loading" class="p-4 text-center">
            <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-700 mx-auto"></div>
            <p class="text-sm text-gray-500 mt-2 font-poppins">Hľadám...</p>
        </div>

        <!-- Results -->
        <div v-else class="max-h-80 overflow-y-auto">
            <!-- Categories Section -->
            <div v-if="results.categories.length > 0" class="p-2">
                <h4 class="text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 py-2">Kategórie</h4>
                <div v-for="(category, index) in results.categories" :key="`category-${category.id}`"
                    class="px-3 py-2 hover:bg-brand-50 cursor-pointer transition-colors flex items-center space-x-3 rounded-lg mx-1"
                    :class="{ 'bg-brand-50': selectedIndex === index }"
                    @click="$emit('select', { type: 'category', data: category })">
                    <div class="w-8 h-8 bg-brand-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Leaf class="h-4 w-4 text-brand-700" />
                    </div>
                    <span class="text-sm font-medium text-gray-900 font-poppins" v-html="highlight(category.name, query)"></span>
                </div>
            </div>

            <!-- Sets Section -->
            <div v-if="results.sets.length > 0" class="p-2 border-t border-gray-100">
                <h4 class="text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 py-2">Sety</h4>
                <div v-for="(set, index) in results.sets" :key="`set-${set.id}`"
                    class="px-3 py-2 hover:bg-amber-50 cursor-pointer transition-colors flex items-center space-x-3 rounded-lg mx-1"
                    :class="{ 'bg-amber-50': selectedIndex === (results.categories.length + index) }"
                    @click="$emit('select', { type: 'set', data: set })">
                    <div class="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                        <img v-if="set.main_image" :src="set.main_image" :alt="set.name" class="w-full h-full object-cover" />
                        <span v-else class="text-amber-600 text-lg">📦</span>
                    </div>
                    <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-gray-900 font-poppins truncate" v-html="highlight(set.name, query)"></p>
                        <div class="flex items-center justify-between mt-1">
                            <span class="text-sm font-semibold text-amber-600">{{ formatPrice(set.price) }}</span>
                            <span class="text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full font-medium">Set</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Products Section -->
            <div v-if="results.products.length > 0" class="p-2 border-t border-gray-100">
                <h4 class="text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 py-2">Produkty</h4>
                <div v-for="(product, index) in results.products" :key="`product-${product.id}`"
                    class="px-3 py-2 hover:bg-brand-50 cursor-pointer transition-colors flex items-center space-x-3 rounded-lg mx-1"
                    :class="{ 'bg-brand-50': selectedIndex === (results.categories.length + results.sets.length + index) }"
                    @click="$emit('select', { type: 'product', data: product })">
                    <img :src="product.main_image" :alt="product.name"
                        class="w-10 h-10 object-cover rounded-lg border border-gray-200 flex-shrink-0">
                    <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-gray-900 font-poppins truncate" v-html="highlight(product.name, query)"></p>
                        <div class="flex items-center justify-between mt-1">
                            <span class="text-sm font-semibold text-brand-700">{{ formatPrice(product.price) }}</span>
                            <span v-if="product.stock_quantity <= 0"
                                class="text-xs text-red-600 font-medium bg-red-50 px-2 py-0.5 rounded-full">Nedostupné</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- View All Results -->
            <div class="p-3 bg-gray-50 border-t border-gray-100">
                <button @click="$emit('search-all')"
                    class="w-full text-left text-sm text-brand-700 hover:text-brand-800 font-medium font-poppins flex items-center justify-between p-2 hover:bg-white rounded-lg transition-colors">
                    <span>Všetky výsledky pre "{{ query }}"</span>
                    <ArrowRight class="h-4 w-4" />
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { useB2BPricing } from '@/composables/useB2BPricing'
import { ArrowRight, Leaf } from 'lucide-vue-next'

defineProps({
    results: { type: Object, required: true },
    query: { type: String, default: '' },
    selectedIndex: { type: Number, default: -1 },
    loading: { type: Boolean, default: false },
    // highlightMatch() of useSearchDropdown, returns escaped HTML
    highlight: { type: Function, required: true },
})

defineEmits(['select', 'search-all'])

const { formatPrice } = useB2BPricing()
</script>
